import { useState } from 'react'
import Checkbox from './Checkbox'
import LoadingSpinner from './LoadingSpinner'
import ProgressBar from './ProgressBar'
import TrashIcon from './icons/TrashIcon'
import classNames from 'classnames'
import { addWebsiteScanner, deleteWebsiteScanner, refreshWebsiteScanners } from '@/src/useWebsiteScanner'


export default function WebsiteContentSection({ scanners, localTopics, onChange, reloadTopics }) {
  const [urlInput, setUrlInput] = useState('')
  const [adding, setAdding] = useState(false)
  const [addProgress, setAddProgress] = useState(0)
  const [addError, setAddError] = useState(null)


  function toggleTopic(topic) {
    onChange(allTopics => {
      return allTopics.map(t => t.id === topic.id ? { ...t, enabled: !t.enabled } : t)
    })
  }


  async function handleDelete(topic) {
    const scannerId = topic.websiteScannerId
    if (!scannerId) return

    try {
      await deleteWebsiteScanner(scannerId)
      onChange(allTopics => allTopics.filter(t => t.id !== topic.id))
      await reloadTopics()
    } catch (err) {
      console.error('Failed to delete website scanner', err)
    }
  }


  async function handleAdd(e) {
    e.preventDefault()
    const url = urlInput.trim()
    if (!url) return

    setAdding(true)
    setAddError(null)
    setAddProgress(0)

    try {
      const task = await addWebsiteScanner(url)
      await task.done(p => setAddProgress(p))
      await refreshWebsiteScanners()
      await reloadTopics()
      setUrlInput('')
    } catch (err) {
      setAddError(err.message || 'Failed to scan website. Check the URL and try again.')
      await refreshWebsiteScanners()
      await reloadTopics()
    } finally {
      setAdding(false)
    }
  }


  function handleKeyDown(e) {
    if (e.key === 'Enter') handleAdd(e)
  }


  const scannerById = {}
  if (scanners) {
    scanners.forEach(s => { scannerById[s.id] = s })
  }


  return (
    <div className="avoid-column-break">
      <div className="text-left category-group bg-white border border-gray px-4 py-1 rounded-lg shadow mb-4">
        <div className="flex-row spread border-bottom my-1 pb-1">
          <div className="gray italics py-1">
            Website Content
          </div>
        </div>

        <div className="py-1">
          {localTopics.map(topic => {
            const scanner = scannerById[topic.websiteScannerId]
            const isScanning = scanner && !scanner.lastScanAt && !scanner.lastError
            const hasError = scanner && scanner.lastError && scanner.consecutiveFailures > 0
            const checked = topic.enabled

            return (
              <div key={topic.id} className="scanner-item">
                <Checkbox
                  alignTop
                  checked={checked}
                  onChange={() => toggleTopic(topic)}
                >
                  <span className={classNames({ gray: !checked, strike: !checked })}>
                    {topic.displayName || topic.name}
                  </span>
                  {scanner?.lastScanAt && (
                    <a
                      href={scanner.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="scanner-url"
                      onClick={e => e.stopPropagation()}
                    >
                      {scanner.url}
                    </a>
                  )}
                  {isScanning && (
                    <span className="scanning-badge">
                      <LoadingSpinner size="small" /> Scanning…
                    </span>
                  )}
                  {hasError && (
                    <span className="error-badge has-text-danger" title={scanner.lastError}>
                      ⚠ scan error
                    </span>
                  )}
                </Checkbox>
                <button
                  className="delete-btn"
                  title="Remove website scanner"
                  onClick={() => handleDelete(topic)}
                >
                  <TrashIcon />
                </button>
              </div>
            )
          })}
        </div>

        <div className="py-2">
          {adding ? (
            <div className="flex-col gap-1">
              <p className="gray small mb-1">Scanning website…</p>
              <ProgressBar value={addProgress} />
            </div>
          ) : (
            <>
              <div className="flex-row gap-1">
                <input
                  className="input"
                  type="text"
                  value={urlInput}
                  onChange={e => setUrlInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add a website URL"
                />
                <button
                  className={classNames('button', { 'is-primary': urlInput.trim().length > 0 })}
                  style={{ padding: '0.5rem' }}
                  onClick={handleAdd}
                  disabled={!urlInput.trim()}
                >
                  Add
                </button>
              </div>
              {addError && (
                <p className="has-text-danger small mt-1">{addError}</p>
              )}
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .scanner-item {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          padding: 0.25rem 0;
        }
        .delete-btn {
          background: none;
          border: none;
          padding: 0;
          color: #ccc;
          cursor: pointer;
          flex-shrink: 0;
          line-height: 1;
          display: flex;
          align-items: center;
        }
        .delete-btn:hover {
          color: #e53e3e;
        }
        .scanner-url {
          display: block;
          font-size: 0.7rem;
          color: #999;
          text-decoration: none;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 18rem;
        }
        .scanner-url:hover {
          text-decoration: underline;
          color: #666;
        }
        .scanning-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          margin-left: 0.25rem;
          font-size: 0.8rem;
          color: #888;
          vertical-align: middle;
        }
        .error-badge {
          margin-left: 0.25rem;
          font-size: 0.8rem;
        }
        .small {
          font-size: 0.85rem;
        }
        .mt-1 {
          margin-top: 0.25rem;
        }
      `}</style>
    </div>
  )
}
